/** First-party analytics — POSTs to our own /api/events.
 *
 * - No cookies; sessionStorage holds an ephemeral session id (lasts until tab close).
 * - sendBeacon (where available) so the request survives page navigations.
 * - Fire-and-forget; failures are silent so analytics never break a page.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const API_BASE = import.meta.env.VITE_API_BASE;
const SESSION_KEY = "tlp.session_id";

// Updated by SessionProvider whenever the /me payload arrives — when true,
// track() short-circuits so admin sessions don't pollute the analytics.
let _isAdmin = false;
export function setAnalyticsAdminFlag(isAdmin: boolean) { _isAdmin = !!isAdmin; }

function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

export function track(name: string, props?: Record<string, string | number | boolean>) {
  if (!name) return;
  if (_isAdmin) return;

  // Strip query strings — they often hold OAuth codes, Stripe session ids,
  // and other transient values we don't want in analytics.
  const path = location.pathname;
  // Don't bother logging the OAuth handoff route — it's a transient redirect.
  if (path === "/auth/callback") return;

  const payload = JSON.stringify({
    name,
    path,
    referrer: document.referrer || null,
    session_id: sessionId(),
    props: props ?? null,
  });
  const url = `${API_BASE}/events`;
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
  } catch { /* fall through */ }
  // Fallback for environments without sendBeacon (or when it returns false).
  try {
    fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch { /* swallow */ }
}

/** Mount once near the router root — fires `pageview` on every navigation. */
export function usePageviewTracking() {
  const loc = useLocation();
  useEffect(() => {
    track("pageview");
    // Track only on path changes; query-string-only changes don't get an
    // extra pageview (analytics already strips ?... from the recorded path).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);
}
